import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {format} from 'date-fns';
const {FiMapPin,FiLayers,FiClock,FiExternalLink,FiAlertTriangle,FiDroplet,FiWind,FiActivity,FiTrendingUp}=FiIcons;

const LatestCalamityCard=({calamities})=> {
  if (!calamities || calamities.length===0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-gray-300">
        <div className="text-center text-gray-500">
          <SafeIcon icon={FiActivity} className="text-3xl mx-auto mb-2 opacity-50" />
          <p>No major calamities detected</p>
        </div>
      </div>
    );
  }

  const getCalamityIcon=(type,eventType,magnitude)=> {
    switch (type) {
      case 'earthquake': return '🌍';
      case 'flood': return '🌊';
      case 'tidal': return eventType==='high_tide' ? '⬆️' : '⬇️';
      case 'weather': 
        if (eventType==='tropical_depression') return '🌀';
        if (eventType==='monsoon') return '🌧️';
        return '⛈️';
      case 'tsunami': return '🌊';
      case 'volcanic': return '🌋';
      default: return '⚠️';
    }
  };

  const getCalamityConfig=(calamity)=> {
    switch (calamity.type) {
      case 'earthquake':
        if (calamity.magnitude >=7) return {color: 'text-red-600 border-red-500',bgColor: 'bg-red-50'};
        if (calamity.magnitude >=6) return {color: 'text-orange-600 border-orange-500',bgColor: 'bg-orange-50'};
        if (calamity.magnitude >=5) return {color: 'text-yellow-600 border-yellow-500',bgColor: 'bg-yellow-50'};
        return {color: 'text-gray-600 border-gray-500',bgColor: 'bg-gray-50'};
      case 'flood': return {color: 'text-blue-600 border-blue-500',bgColor: 'bg-blue-50'};
      case 'tidal': return {color: 'text-cyan-600 border-cyan-500',bgColor: 'bg-cyan-50'};
      case 'weather': return {color: 'text-purple-600 border-purple-500',bgColor: 'bg-purple-50'};
      case 'tsunami': return {color: 'text-red-600 border-red-500',bgColor: 'bg-red-50'};
      case 'volcanic': return {color: 'text-orange-600 border-orange-500',bgColor: 'bg-orange-50'};
      default: return {color: 'text-gray-600 border-gray-500',bgColor: 'bg-gray-50'};
    }
  };

  const getAlertBadge=(alert)=> {
    const configs={
      'Warning': {color: 'bg-red-100 text-red-800',icon: FiAlertTriangle},
      'Watch': {color: 'bg-amber-100 text-amber-800',icon: FiAlertTriangle},
      'Advisory': {color: 'bg-blue-100 text-blue-800',icon: FiAlertTriangle},
      'None': {color: 'bg-gray-100 text-gray-800',icon: null},
    };
    const config=configs[alert] || configs['None'];
    if (alert==='None') return null;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.icon && <SafeIcon icon={config.icon} className="mr-1 text-xs" />}
        {alert}
      </span>
    );
  };

  const formatTime=(timeString)=> {
    const date=new Date(timeString);
    return {
      pht: format(date,'MMM dd,yyyy HH:mm:ss') + ' PHT',
      utc: format(date,'HH:mm:ss') + ' UTC'
    };
  };

  // Filter earthquakes to only show those in the last 3 hours
  const recentEarthquakes=calamities
    .filter(c=> c.type==='earthquake' && 
      (new Date() - new Date(c.time_utc)) / (1000 * 60 * 60) <= 3)
    .sort((a,b)=> new Date(b.time_utc) - new Date(a.time_utc));

  const latestOtherMajorCalamity=calamities
    .filter(c=> c.type !=='earthquake' && c.alert !=='None')
    .sort((a,b)=> new Date(b.time_utc) - new Date(a.time_utc))[0];

  const renderSpecificInfo=(calamity)=> {
    switch (calamity.type) {
      case 'earthquake':
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiLayers} className="text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Depth: {calamity.depth_km.toFixed(1)} km</div>
                <div className="text-xs text-gray-600">Type: {calamity.event_type}</div>
              </div>
            </div>
          </div>
        );
      case 'flood':
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiDroplet} className="text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Water Level: {calamity.flood_info?.water_level_m} m</div>
                <div className="text-xs text-gray-600">Affected Area: {calamity.flood_info?.affected_area}</div>
                {calamity.flood_info?.evacuation_advised && (
                  <div className="text-xs text-red-600 font-medium">⚠️ Evacuation Advised</div>
                )}
              </div>
            </div>
          </div>
        );
      case 'tidal':
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiDroplet} className="text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">{calamity.tidal_info?.type} Tide: {calamity.tidal_info?.height_m} m</div>
                <div className="text-xs text-gray-600">Next Tide: {format(new Date(calamity.tidal_info?.next_tide),'HH:mm')} PHT</div>
              </div>
            </div>
          </div>
        );
      case 'weather':
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiWind} className="text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Wind: {calamity.weather_info?.wind_speed_kph} kph</div>
                <div className="text-xs text-gray-600">Rainfall: {calamity.weather_info?.rainfall_mm} mm</div>
                {calamity.weather_info?.signal_number > 0 && (
                  <div className="text-xs text-orange-600 font-medium">Signal #{calamity.weather_info.signal_number}</div>
                )}
              </div>
            </div>
          </div>
        );
      case 'volcanic':
        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiActivity} className="text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Activity: {calamity.volcano_info?.activity_type}</div>
                <div className="text-xs text-gray-600">Alert Level: {calamity.volcano_info?.alert_level}</div>
              </div>
            </div>
          </div>
        )
      default:
        return null;
    }
  };

  const CalamityTile=({calamity,defaultType,defaultIcon})=> {
    if (!calamity) {
      return (
        <motion.div 
          initial={{opacity: 0,scale: 0.95}} 
          animate={{opacity: 1,scale: 1}} 
          className="bg-gray-50 rounded-lg shadow-sm p-4 border border-gray-200"
        >
          <div className="text-center text-gray-500">
            <span className="text-2xl opacity-50">{defaultIcon}</span>
            <h3 className="font-medium text-sm mt-2">No Active {defaultType}</h3>
            <p className="text-xs mt-1">All clear</p>
          </div>
        </motion.div>
      );
    }

    const time=formatTime(calamity.time_utc);
    const config=getCalamityConfig(calamity);
    const tileIcon=getCalamityIcon(calamity.type,calamity.event_type,calamity.magnitude);
    const locationName=calamity.place_text?.split(' of ')[1]?.split(',')[0] || calamity.nearest_city?.name || 'Offshore';

    return (
      <motion.div 
        initial={{opacity: 0,scale: 0.95}} 
        animate={{opacity: 1,scale: 1}} 
        className={`bg-white rounded-lg shadow-md p-4 border-l-4 ${config.color.split(' ')[1]} ${config.bgColor} hover:shadow-lg transition-shadow cursor-pointer`}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 mb-1 flex items-center space-x-2">
              <span className="text-xl">{tileIcon}</span>
              <div>
                <span className="text-sm capitalize">{calamity.type}</span>
                <div className="text-xs text-gray-500 font-normal">near <span className="font-bold text-red-600 text-base">{locationName}</span></div>
              </div>
            </h3>
            {getAlertBadge(calamity.alert)}
          </div>
          <div className="text-right">
            {calamity.type==='earthquake' ? (
              <div className={`text-2xl font-bold ${config.color}`}>M{calamity.magnitude.toFixed(1)}</div>
            ) : calamity.type==='volcanic' ? (
              <div className={`text-md font-bold ${config.color}`}>Alert Lvl {calamity.magnitude}</div>
            ) : (
              <div className={`text-md font-bold ${config.color}`}>{calamity.event_type?.replace('_',' ') || 'Active'}</div>
            )}
            <div className="text-xs text-gray-600">{calamity.event_type?.replace('_',' ')}</div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-start space-x-2">
            <SafeIcon icon={FiClock} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <div className="font-medium text-gray-900">{time.pht.split(',')[0]}</div>
              <div className="text-gray-600">{time.pht.split(',')[1]}</div>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <SafeIcon icon={FiMapPin} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <div className="font-medium text-gray-900">{calamity.nearest_city ? `${calamity.nearest_city.distance_km} km from ${calamity.nearest_city.name}` : 'Offshore location'}</div>
              <div className="text-gray-600">{calamity.province}</div>
            </div>
          </div>
          {renderSpecificInfo(calamity)}
        </div>
        {calamity.detail_url && (
          <button 
            onClick={()=> window.open(calamity.detail_url,'_blank')} 
            className="mt-3 w-full text-xs text-blue-600 hover:text-blue-800 transition-colors flex items-center justify-center space-x-1"
          >
            <span>View Details</span>
            <SafeIcon icon={FiExternalLink} className="text-xs" />
          </button>
        )}
      </motion.div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Active Calamities</h2>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <SafeIcon icon={FiTrendingUp} />
          <span>Real-time monitoring</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CalamityTile 
          calamity={recentEarthquakes.length > 0 ? recentEarthquakes[0] : null} 
          defaultType="Earthquakes" 
          defaultIcon="🌍" 
        />
        <CalamityTile 
          calamity={latestOtherMajorCalamity} 
          defaultType="Events" 
          defaultIcon="🌊" 
        />
      </div>
      {recentEarthquakes.length > 1 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-sm text-blue-800">
            <strong>Additional Earthquakes:</strong> {recentEarthquakes.length - 1} more earthquake{recentEarthquakes.length - 1 > 1 ? 's' : ''} detected in the last 3 hours.
          </div>
        </div>
      )}
    </div>
  );
};

export default LatestCalamityCard;